/**
 * Signing in to Gun with the blinded alias.
 *
 * The account is reached with derived values only: `alias` and `seaPassword` from
 * `deriveSyncIdentity`. Neither the username nor the passphrase the user typed
 * appears here or anywhere downstream.
 *
 * ## Register or log in, decided by trying
 *
 * A device cannot know whether this is the first device for an account or the
 * second, and asking the user would be asking them to remember something they have
 * no reason to know. So it attempts to log in, and creates the account only if no
 * account exists.
 *
 * The order matters and is not the obvious one. Creating first and falling back to
 * login would race the second device: two devices setting up at once would both
 * find nothing, both create, and one would overwrite the other's keypair — leaving
 * a device holding an identity that no longer opens the account, with no error
 * anywhere.
 */
import {
  gunLogin,
  gunRegisterAndLogin,
  setDeviceRegistrationEnabled,
} from '@shared/data/gun'
import type { SyncIdentity } from './syncIdentity'

/*
 * Turned off at module load, before anything here can sign in.
 *
 * The lifted layer registers the device on every successful login, writing
 * `{id, name, ua, lastSeen}` to a public node — a user-agent string and a
 * timestamp per device. `NAMESPACE-MAP.md` states that Thana writes no device
 * registry and no presence record; without this line that statement was false the
 * moment anyone enabled sync, and it was found by reading a real relay rather
 * than by any test.
 *
 * At module scope rather than inside the function because it must hold for every
 * path into the layer, including any future caller that signs in without going
 * through `gunLoginWithIdentity`.
 */
setDeviceRegistrationEnabled(false)

export type SignInResult = { ok: true; pub: string } | { ok: false; error: string }

export async function gunLoginWithIdentity(identity: SyncIdentity): Promise<SignInResult> {
  const existing = await gunLogin(identity.alias, identity.seaPassword)
  if (existing.ok) return { ok: true, pub: existing.pub }

  const created = await gunRegisterAndLogin(identity.alias, identity.seaPassword)
  if (created.ok) return { ok: true, pub: created.pub }

  /*
   * Reported as a value, matching the layer's own convention.
   *
   * Every failure here is a normal outcome of talking to a relay over a flaky
   * socket — offline, timed out, relay refusing the write. Modelling those as
   * exceptions would put a try/catch around sign-in in every caller, and the
   * honest response to all of them is the same: sync does not start, entries stay
   * safe locally, try again later.
   */
  return { ok: false, error: created.error }
}
